import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
// import { FileInputValue } from '@ngx-dropzone/cdk';
import { LoanDocument } from 'app/models/borrower/borrower-document.model';
import { LoansService } from 'app/modules/loan/loans/loans.service';
import { ToastrService } from 'ngx-toastr';
import { FileViewerComponent } from '../file-viewer/file-viewer.component';
import { ConfirmDialogComponent } from 'app/shared/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-borrower-document',
  templateUrl: './borrower-document.component.html',
  styleUrls: ['./borrower-document.component.scss']
})
export class BorrowerDocumentComponent {
  @Input() loanDocuments!: LoanDocument[];
  @Input() loanNumber: any;
  @Input() userType: any;
  @Output() downloadFile = new EventEmitter<LoanDocument>();
  maxSizeInMB = 10;
  acceptedFileTypes = '.pdf,.jpg,.jpeg,.png,.doc,.docx,application/pdf,image/jpeg,image/png,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  private readonly allowedDocumentMessage = 'Only PDF, JPG, JPEG, PNG, DOC and DOCX files are allowed.';
  private readonly allowedMimeTypes = [
    'application/pdf',
    'image/jpeg',
    'image/jpg',
    'image/png',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/zip',
    'application/x-zip-compressed',
    'application/octet-stream'
  ];
  private readonly allowedExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx'];
  private readonly macroExtensions = ['docm', 'dotm'];
  panelOpenState = false;
  files: File[] = [];
  dataSource: LoanDocument[] = [];
  allUserDocuments: LoanDocument[] = [];
  documentStateLoaded = false;
  documentType!: string;
  displayedColumns: string[] = ['loanNumber', 'documentName', 'uploadUser', 'download'];

  uploadSectionIdentityId = false;
  uploadSectionFeeAgreement = false;
  uploadSectionRent = false;
  uploadSectionUtilityBills = false;
  uploadSectionBankStatement = false;
  uploadSectionPhoneBill = false;

  checkIdentityID = false;
  checkFeeAgreement = false;
  checkRent = false;
  checkUtilityBills = false;
  checkBankStatement = false;
  checkPhoneBill = false;


  get allDocumentsUploaded(): boolean {
  return this.checkIdentityID &&
         this.checkFeeAgreement &&
         this.checkRent &&
         this.checkUtilityBills &&
         this.checkBankStatement &&
         this.checkPhoneBill;
}

  /**
   * Constructor
   */
  constructor(private _router: Router,
    private _toastrService: ToastrService,
    private _loanService: LoansService,
    private route: ActivatedRoute,
    public dialog: MatDialog
  ) {
  }


  ngOnInit(): void {
    this.documentType = 'IdentityId';
    this.refreshAllDocumentChecks(this.documentType);
  }

  clearFile(documentType: any) {
    if (this.documentType != documentType) {
      this.documentType = documentType;
      this.files = [];
    }
    this.updateDataSourceForType(documentType);
    if (!this.documentStateLoaded) {
      this.refreshAllDocumentChecks(documentType, true);
    }
  }

  onSelect(event : any) {
    if (event.rejectedFiles?.length) {
      const rejectedName = event.rejectedFiles[0]?.name || '';
      const rejectedExt = (rejectedName.split('.').pop() || '').toLowerCase();
      this._toastrService.error(
        this.macroExtensions.includes(rejectedExt)
          ? 'Word documents with macros are not allowed.'
          : this.allowedDocumentMessage
      );
      return;
    }

    const addedFile = event.addedFiles?.[0];
    if (!addedFile) {
      return;
    }

    const maxSizeBytes = this.maxSizeInMB * 1024 * 1024;
    
    if (addedFile.size > maxSizeBytes) {
      this._toastrService.error("File size allow 10MB only");
      return;
    }

    const extension = (addedFile.name.split('.').pop() || '').toLowerCase();
    if (this.macroExtensions.includes(extension)) {
      this._toastrService.error('Word documents with macros are not allowed.');
      return;
    }
    const mimeOk = !addedFile.type || this.allowedMimeTypes.includes(addedFile.type);
    if (!mimeOk || !this.allowedExtensions.includes(extension)) {
      this._toastrService.error(this.allowedDocumentMessage);
      return;
    }

    let file = this.files.filter(x => x.name == addedFile.name);
    if (file.length == 0) {
      this.files.push(...event.addedFiles);
    }
    else {
      this._toastrService.error("Duplicate file not allowed.");
      return;
    }
  }

  onRemove(event : any) {
    this.files.splice(this.files.indexOf(event), 1);
  }

  UploadDocument(documentType : any) {

    let loanNumber = this.route.snapshot.paramMap.get('loanNumber');
    this.files.forEach(x => {
      const formData = new FormData();
      formData.append("formFile", x);
      this._loanService.apiPostDocument(formData, loanNumber, documentType, this.userType)
        .subscribe((response) => {
          this.refreshAllDocumentChecks(documentType, true);
          this._toastrService.success(response.data.message);
          this.showFileUploadSection('');
        },
          (error) => {
            this._toastrService.error(error?.error?.failureMessage || "Error uploading document");
          });
    });
  }

  getUploadedDocument(documentType : any) {
    this.documentType = documentType;
    this.updateDataSourceForType(documentType);

    if (!this.documentStateLoaded) {
      this.refreshAllDocumentChecks(documentType, true);
    }
  }

  refreshAllDocumentChecks(activeDocumentType?: string, showWarningOnError = false) {
    const loanNumber = this.route.snapshot.paramMap.get('loanNumber');

    this._loanService.apiGetDocument(loanNumber, this.userType)
      .subscribe((response) => {
        const documentList = response?.data?.documentList ?? [];
        this.allUserDocuments = documentList;
        this.documentStateLoaded = true;
        this.applyDocumentChecks(documentList);

        if (activeDocumentType) {
          this.documentType = activeDocumentType;
        }

        if (this.documentType) {
          this.updateDataSourceForType(this.documentType);
        }
      },
      (error) => {
        console.log(error);
        if (showWarningOnError) {
          this._toastrService.warning('Unable to refresh document status. Please retry.');
        }
      });
  }

  applyDocumentChecks(documentList: LoanDocument[]) {
    const identityIdCount = this.countByDocumentType(documentList, 'IdentityId');
    const feeAgreementCount = this.countByDocumentType(documentList, 'FeeAgreement');
    const rentCount = this.countByDocumentType(documentList, 'Rent');
    const utilityBillsCount = this.countByDocumentType(documentList, 'UtilityBills');
    const bankStatementCount = this.countByDocumentType(documentList, 'BankStatement');
    const phoneBillCount = this.countByDocumentType(documentList, 'PhoneBill');

    this.checkIdentityID = identityIdCount > 0;
    this.checkFeeAgreement = feeAgreementCount > 0;
    this.checkRent = rentCount >= 3;
    this.checkUtilityBills = utilityBillsCount >= 3;
    this.checkBankStatement = bankStatementCount >= 3;
    this.checkPhoneBill = phoneBillCount >= 3;
  }

  countByDocumentType(documentList: LoanDocument[], documentType: string): number {
    return documentList.filter((element: any) => element.documentType === documentType).length;
  }

  updateDataSourceForType(documentType: string) {
    this.dataSource = this.allUserDocuments.filter((element: any) => element.documentType === documentType);
  }


  downloadDocument(element: any) {
  
    this._loanService.apiDownloadDocument(element.documentId)
      .subscribe((response) => {
      

        //console.log(response.data.file.Body.data);
        const filename = element.documentName;
        //const blob = new Blob([response.data.file.Body], {type: 'image/png'});
        //let blob : Blob = response.body as Blob;
        var a = document.createElement('a');
        a.setAttribute('href', response.data.file);
        document.body.appendChild(a);
        a.setAttribute('download', filename);
        //a.setAttribute('target', '_blank');
        a.click();
      },
        (error) => {
          console.log(error);
        })
  }

  previewDocument(element: any) {
  this._loanService.apiPreviewDocument(element.documentId).subscribe(
    (response) => {
      this.openDialog(response.data.file, response.data.fileName || element.documentName);
    },
    (error) => {
      console.log(error);
    }
  );
}


  dialogRef!: MatDialogRef<FileViewerComponent>;
  openDialog(fileUrl: string, documentName: string) {
  this.dialogRef = this.dialog.open(FileViewerComponent, {
    width: '70%',
    height: '100%',
    data: {
      file: fileUrl,
      fileName: documentName
    }
  });

  this.dialogRef.afterClosed().subscribe(res => {
  });
}


  onDeleteFile(element: any, documentType: string) {
    console.log('Delete document:', element);
  const dialogRef = this.dialog.open(ConfirmDialogComponent, {
    width: '350px',
    data: {
      title: 'Confirm Deletion',
      message: 'Are you sure you want to delete this document with Name: ' + '"'+element.documentName+'"' + '?',
    }
  });

  dialogRef.afterClosed().subscribe(result => {
    if (result) {
      // Usuario confirmó → ejecutar eliminación
      this._loanService.apiPostDocumentDelete(element.documentId).subscribe(
        (response) => {
          if (response) {
            this._toastrService.success("Document deleted successfully");
            this.refreshAllDocumentChecks(documentType, true);
          }
        },
        (error) => {
          this._toastrService.error(error.error.failureMessage);
        }
      );
    } else {
      // Usuario canceló → no hacer nada
      this._toastrService.info("Deletion cancelled");
    }
  });
}


  showFileUploadSection(documentType: any) {
    this.uploadSectionPhoneBill = false;
    this.uploadSectionBankStatement = false;
    this.uploadSectionUtilityBills = false;
    this.uploadSectionRent = false;
    this.uploadSectionFeeAgreement = false;
    this.uploadSectionIdentityId = false;
    if (documentType === 'PhoneBill') {
      this.files = [];
      this.uploadSectionPhoneBill = true;
    }
    else if (documentType === 'BankStatement') {
      this.files = [];
      this.uploadSectionBankStatement = true;
    }
    else if (documentType === 'UtilityBills') {
      this.files = [];
      this.uploadSectionUtilityBills = true;
    }
    else if (documentType === 'Rent') {
      this.files = [];
      this.uploadSectionRent = true;
    }
    else if (documentType === 'FeeAgreement') {
      this.files = [];
      this.uploadSectionFeeAgreement = true;
    }
    else if (documentType === 'IdentityId') {
      this.files = [];
      this.uploadSectionIdentityId = true;
    }
  }
}
